import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import {
    useDeleteSubscriptionMutation,
    useGetCapitalAccountsQuery,
    useGetCapitalCallsQuery,
    useLazyGetCurrentUserQuery,
    useGetClosingsQuery,
    useGetShareSubcategoriesQuery,
    useGetInvestmentFundsQuery,
    useGetNAVQuery,
} from 'api/main';
import { userSelectors } from 'reducers';
import { stepIDs, subscriptionStatus, paymentMethods } from 'utils/constants';
import {
    parseInvestmentFundName,
    parseSum,
    parseNumber,
    ucFirst,
    parseFullName,
    getPaymentMethodText,
} from 'utils/helpers';
import {
    useInvestorPortfolioData,
    useShareSubCategory,
    useSubscriptionFormSignatureStatus,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import PageWrapper from './PageWrapper';
import { InfoCard } from 'containers/dashboard/common-components';
import {
    CancelButton,
    ConfirmDialog,
    DataTile,
    InfoDialog,
    Loader,
    SubmitButton,
} from 'components';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from '@mui/material';

const infoCardProps = {
    disableGrid: true,
    width: '80%',
    flexBase: '40%',
};

const InvestmentRow = ({ signedDate, investmentFundName, sum, infoLabel }) => {
    const sumWithSign = sum === 0 ? parseSum(sum) : `-${parseSum(sum)}`;
    return (
        <TableRow>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginRight: '60px',
                        color: 'grey.700',
                    }}
                >
                    {signedDate ? signedDate.format('L') : ''}
                </Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginRight: '60px',
                        color: 'grey.700',
                    }}
                >
                    {infoLabel}
                </Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginRight: '60px',
                        color: 'common.black',
                    }}
                >
                    {investmentFundName}
                </Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '16px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                        color: 'error.main',
                        '@media (max-width: 600px)': {
                            paddingRight: '20px',
                        },
                    }}
                >
                    {sumWithSign}
                </Typography>
            </TableCell>
        </TableRow>
    );
};

const InvestmentGroup = ({
    signedDate,
    investmentFundName,
    paymentMethod,
    entryFees,
    premiumAmount,
    totalAmount,
}) => {
    const signedDatePlus10 = moment(signedDate).add(10, 'days');
    const displayPremiumAmount = useMemo(
        () => premiumAmount !== null && !Number.isNaN(premiumAmount) && premiumAmount > 0,
        [premiumAmount]
    );
    return (
        <TableContainer
            sx={{
                overflow: 'auto',
                padding: '0 60px',
                '@media (max-width: 600px)': {
                    padding: '0 20px',
                },
            }}
        >
            <Table>
                <TableBody>
                    <InvestmentRow
                        signedDate={signedDate}
                        infoLabel={texts.ADMISSION_FEES}
                        investmentFundName={investmentFundName}
                        sum={entryFees}
                    />
                    {displayPremiumAmount && (
                        <InvestmentRow
                            signedDate={signedDate}
                            infoLabel={texts.SUBSCRIPTION_COMMISSION_VALUE}
                            investmentFundName={investmentFundName}
                            sum={premiumAmount}
                        />
                    )}
                    <InvestmentRow
                        signedDate={signedDatePlus10}
                        infoLabel={
                            paymentMethod === paymentMethods.DIRECT_DEBIT
                                ? texts.DIRECT_DEBIT
                                : texts.CREDIT_TRANSFER
                        }
                        investmentFundName={investmentFundName}
                        sum={totalAmount}
                    />
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const InvestorDashboard = () => {
    const dispatch = useDispatch();

    const [newSubscriptionOpen, setNewSubscriptionOpen] = useState(false);
    const [disclaimerDialogOpen, setDisclaimerDialogOpen] = useState(false);
    const [cancelSubscriptionOpen, setCancelSubscriptionOpen] = useState(false);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription); // first finished subscription
    const lastFinishedSubscription = useSelector(userSelectors.getLastFinishedSubscription);
    const {
        investorProfile = {},
        impersonatedByUser,
        subscriptions = [],
        ...internalUser
    } = useSelector(userSelectors.getInternalUser);
    const { shareValue, investingSum } = lastFinishedSubscription.investmentCharacteristics;

    const [getCurrentUser] = useLazyGetCurrentUserQuery();
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery();
    const { data: investmentFunds = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery();
    const [deleteSubscription, { isSuccess: deleteSuccessful, isLoading: deleteLoader }] =
        useDeleteSubscriptionMutation();
    const { data: capitalCalls = [], isLoading: capitalCallsLoader } = useGetCapitalCallsQuery({
        status: ['completed', 'activated'],
    });
    const { data: capitalAccounts = [], isLoading: capitalAccountsLoader } =
        useGetCapitalAccountsQuery({});
    const { data: closings = [], isLoading: closingsLoader } = useGetClosingsQuery({
        status: ['completed', 'activated'],
    });
    const { data: navs = [] } = useGetNAVQuery({
        pagination: false,
        status: ['completed', 'activated'],
    });

    const finishedSubscriptions = useMemo(
        () =>
            subscriptions.filter(
                s =>
                    s.subscriptionStatus === subscriptionStatus.FINISHED && s.investmentFund.visible
            ),
        [subscriptions]
    );

    const currentInvestmentFund = useMemo(
        () => investmentFunds.find(f => f.id === currentSubscription?.investmentFund?.id),
        [currentSubscription, investmentFunds]
    );

    const totalInvestedSum = useMemo(
        () =>
            finishedSubscriptions.reduce(
                (acc, crt) => acc + crt.investmentCharacteristics.investingSum,
                0
            ),
        [finishedSubscriptions]
    );
    const coSubscriber = useMemo(
        () =>
            subscriptions.find(s => s.subscriptionStatus === subscriptionStatus.FINISHED)
                ?.coSubscriber,
        [subscriptions]
    );
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const hasInProgressSubscription = useSelector(userSelectors.getInProgressSubscriptionStatus);
    const hasSignedBS = useSubscriptionFormSignatureStatus(tunnelSubscription);

    const subscriptionFinished = useMemo(
        () => currentSubscription.subscriptionStatus === subscriptionStatus.FINISHED,
        [currentSubscription]
    );

    const investmentFundName = useMemo(
        () => parseInvestmentFundName(currentSubscription.investmentFund),
        [currentSubscription]
    );

    const signedByServicerDate = useMemo(() => {
        const step = currentSubscription.userTunnelSteps.find(
            us => us.tunnelStep.step.id === stepIDs.SUBSCRIPTION_FORM_SIGNATURE
        );
        if (step?.signatures?.length === 2) {
            return moment(step.signatures[1].finishedAt);
        }
        return null;
    }, [currentSubscription]);

    const shareSubCategory = useShareSubCategory();
    const paymentMethod = useMemo(
        () => currentSubscription?.bankingStatement?.paymentMethod,
        [currentSubscription]
    );

    const premiumAmount = useMemo(
        () => (investingSum * (shareSubCategory?.subscriptionFee || 0)) / 100,
        [shareSubCategory, investingSum]
    );

    const admissionFee = useMemo(
        () => currentSubscription?.investmentCharacteristics?.admissionFee,
        [currentSubscription]
    );

    const entryFees = useMemo(
        () => (investingSum * admissionFee) / 100,
        [admissionFee, investingSum]
    );

    const totalAmount = useMemo(
        () => investingSum + premiumAmount + entryFees,
        [investingSum, premiumAmount, entryFees]
    );
    const atLeastOneFinishedDirectSubscription = useMemo(
        () => !!subscriptions.find(s => !s.createdBy),
        [subscriptions]
    );

    const totalData = useInvestorPortfolioData({
        finishedSubscriptions,
        capitalAccounts,
        shareSubcategories,
        capitalCalls,
        closings,
        navs,
    });

    const handleCloseNewSubscriptionDialog = useCallback(() => setNewSubscriptionOpen(false), []);
    const handleOpenCancelSubscription = useCallback(() => setCancelSubscriptionOpen(true), []);
    const handleCloseCancelSubscription = useCallback(() => setCancelSubscriptionOpen(false), []);

    const handleStartSubscription = useCallback(() => {
        dispatch(push('/new-subscription'));
    }, [dispatch]);

    const handleCancelSubscription = useCallback(() => {
        deleteSubscription({ id: tunnelSubscription.id, refetchMe: true });
    }, [deleteSubscription, tunnelSubscription]);

    useEffect(() => {
        if (deleteSuccessful) {
            setCancelSubscriptionOpen(false);
            getCurrentUser();
        }
    }, [deleteSuccessful, getCurrentUser]);

    return (
        <PageWrapper
            title={
                <React.Fragment>
                    {`${texts.BONJOUR} ${ucFirst(internalUser?.firstName)}, ${
                        coSubscriber ? ` ${texts.BONJOUR} ${ucFirst(coSubscriber.firstName)},` : ''
                    } `}
                    <br></br>
                    {texts.BIENVENUE_ESPACE_PERSONNEL}
                </React.Fragment>
            }
        >
            {subscriptionFinished ? (
                <Loader
                    loading={
                        capitalAccountsLoader ||
                        subcategoriesLoader ||
                        capitalCallsLoader ||
                        closingsLoader
                    }
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap="16px"
                        mb={4}
                    >
                        <InfoCard
                            number={finishedSubscriptions.length}
                            label={texts.SUBSCRIPTIONS.toUpperCase()}
                            {...infoCardProps}
                        />
                        <InfoCard
                            number={parseSum(totalInvestedSum)}
                            label={texts.TOTAL_ENGAGEMENTS}
                            {...infoCardProps}
                        />
                        <InfoCard
                            number={parseSum(totalData.totalCapitalCalled)}
                            label={texts.CAPITAL_CALLED_TO_DATE}
                            {...infoCardProps}
                        />
                        <InfoCard
                            number={parseSum(totalData.totalValueToDate)}
                            label={texts.LATEST_VALORIZATION}
                            {...infoCardProps}
                        />
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography
                            variant="body2"
                            align="left"
                            gutterBottom
                            sx={{
                                fontSize: '13px',
                                letterSpacing: '1.3px',
                                lineHeight: '16px',
                                color: 'grey.700',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                marginTop: '24px',
                                marginBottom: '10px',
                            }}
                        >
                            {texts.MEMBER_SINCE}
                        </Typography>
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                                marginBottom: '20px',
                                fontSize: '22px',
                                color: 'common.black',
                            }}
                        >
                            {signedByServicerDate ? signedByServicerDate.format('LL') : ''}
                        </Typography>
                    </Box>
                    {/* // TODO: uncomment this when we get the green light */}
                    {false && (
                        <DataTile
                            title={texts.HISTORIQUE_INVESTISSEMENTS}
                            sx={{
                                padding: 0,
                                flexDirection: 'column',
                            }}
                        >
                            <InvestmentGroup
                                signedDate={signedByServicerDate}
                                investmentFundName={investmentFundName}
                                paymentMethod={paymentMethod}
                                entryFees={entryFees}
                                premiumAmount={premiumAmount}
                                totalAmount={totalAmount}
                            />
                        </DataTile>
                    )}
                </Loader>
            ) : (
                <DataTile sx={{ flexDirection: 'column' }}>
                    <Loader loading={fundsLoader}>
                        {currentInvestmentFund?.evergreen ? (
                            <Typography gutterBottom>
                                <b>{parseFullName(internalUser)}</b> {texts.YOU_HAVE_INVESTED}{' '}
                                <b>{parseSum(investingSum)}</b> {texts.INTO_THE_FUND}{' '}
                                <b>{currentSubscription.investmentFund.name}</b>{' '}
                                {texts.COMMERCIALISED_BY}{' '}
                                <b>{currentSubscription.investmentFund.promoter?.name}</b>.{' '}
                                {texts.SGP_WILL_SIGN_BS}{' '}
                                <b>
                                    {getPaymentMethodText(
                                        currentSubscription?.bankingStatement?.paymentMethod
                                    ).toLowerCase()}
                                </b>{' '}
                                {texts.AMOUNTING}{' '}
                                <b>
                                    {parseSum(
                                        investingSum +
                                            (currentSubscription?.investmentCharacteristics
                                                ?.admissionFee /
                                                100) *
                                                investingSum
                                    )}
                                </b>{' '}
                                {texts.YOUR_DOCUMENTS_VALIDATION} {texts.CONFIRMATION_EMAIL_PENDING}
                            </Typography>
                        ) : (
                            <Typography gutterBottom>
                                <b>{parseFullName(internalUser)}</b> {texts.YOU_HAVE_INVESTED}{' '}
                                <b>{parseSum(investingSum)}</b> {texts.REPRESENTING}{' '}
                                <b>{parseNumber(investingSum / shareValue)}</b> {texts.FUND_SHARES}{' '}
                                <b>{currentSubscription.investmentFund.name}</b>{' '}
                                {texts.TEAM_THANKS_YOU(
                                    currentSubscription.investmentFund?.sgp?.name
                                )}
                            </Typography>
                        )}
                        <Typography gutterBottom>
                            {texts.SIGNED_BY_SGP(currentSubscription.investmentFund?.sgp?.name)}
                        </Typography>
                    </Loader>
                </DataTile>
            )}
            {!investorProfile?.usPerson &&
                !hasSignedBS &&
                (!tunnelSubscription?.indirectUntransferred || !!impersonatedByUser) && ( // in progress subscription is not transferred
                    <Box textAlign="center" mb={6}>
                        {hasInProgressSubscription && (
                            <SubmitButton
                                text={texts.CONTINUE_YOUR_SUBSCRIPTION}
                                onClick={() => dispatch(push('/new-subscription'))}
                            />
                        )}
                        {hasInProgressSubscription && (
                            <Typography
                                sx={{ marginTop: 1, textTransform: 'uppercase', fontSize: '14px' }}
                            >
                                {texts.ON_FUND(tunnelSubscription.investmentFund.name)}
                            </Typography>
                        )}
                        {hasInProgressSubscription && atLeastOneFinishedDirectSubscription && (
                            <Box marginY="20px">
                                <CancelButton
                                    variant="contained"
                                    onClick={handleOpenCancelSubscription}
                                >
                                    {texts.CANCEL_ONGOING_SUBSCRIPTION}
                                </CancelButton>
                            </Box>
                        )}
                    </Box>
                )}
            <ConfirmDialog
                open={newSubscriptionOpen}
                loading={deleteLoader}
                title={texts.NEW_SUBSCRIPTION_TITLE}
                confirmText={texts.CONTINUER}
                cancelText={texts.CANCEL}
                handleClose={handleCloseNewSubscriptionDialog}
                handleConfirm={handleStartSubscription}
            >
                <Typography variant="body2">{texts.NEW_SUBSCRIPTION_CONTENT}</Typography>
            </ConfirmDialog>
            <ConfirmDialog
                open={cancelSubscriptionOpen}
                loading={deleteLoader}
                title={texts.CANCEL_SUBSCRIPTION_TITLE}
                confirmText={texts.CONFIRMER}
                cancelText={texts.CANCEL}
                handleClose={handleCloseCancelSubscription}
                handleConfirm={handleCancelSubscription}
            >
                <Typography variant="body2">{texts.CONFIRM_CANCEL_SUBSCRIPTION}</Typography>
            </ConfirmDialog>
            <InfoDialog
                open={disclaimerDialogOpen}
                title={texts.SUBSCRIPTION}
                message={texts.INDIRECT_UBSCRIPTION_DISCLAIMER}
                handleClose={() => setDisclaimerDialogOpen(false)}
            />
        </PageWrapper>
    );
};

export default InvestorDashboard;
